import { Injectable } from '@angular/core';
import { EMPTY, Observable, combineLatest, forkJoin, of, timer } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { ChatService } from '../../../services/chat.service';
import { PosIntegrityService } from '../../../services/pos-integrity.service';
import { TerritoryService } from '../../../services/territory-management.service';
import { UserActivityService } from '../../../services/user-activity.service';
import { SurveyService } from '../../../../modules/pages/store-survey/shared/services/survey.service';
import { Pageable } from '../../../models/pageable';
import { ActivityStatus } from '../../../../shared/models/admin/activity.model';

export interface PosIntegrityBadgeState {
  hasTasks: boolean;
  count: number;
}

@Injectable({
  providedIn: 'root',
})
export class MenuBadgeService {
  private readonly intervalDuration = 30000;

  readonly unreadMessages$: Observable<boolean> = timer(0, this.intervalDuration).pipe(
    switchMap(() => this.whenVisible(
      this.chatSvc.countUnreadMessages().pipe(
        map(value => value.count > 0),
        catchError(() => of(false))
      )
    )),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly posIntegrityBadge$: Observable<PosIntegrityBadgeState> = timer(0, this.intervalDuration).pipe(
    switchMap(() => this.whenVisible(
      this.posIntegrityService.getPosIntegrityCount(this.terrService.getTerritory()).pipe(
        map(value => ({
          hasTasks: value.count !== 0,
          count: value.count,
        })),
        catchError(() => of({
          hasTasks: false,
          count: 0,
        }))
      )
    )),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * True while this territory has an activity still waiting on the user — saved as a draft or never
   * started. Submitted ones are excluded so the badge clears once there is nothing left to do.
   *
   * The user endpoint takes a single status, so NEW and IN_PROGRESS are asked for separately; each is
   * a one-row page, fetched only for its `totalElements`.
   */
  readonly userActivitiesBadge$: Observable<boolean> = timer(0, this.intervalDuration).pipe(
    switchMap(() => this.whenVisible(
      forkJoin([
        this.hasActivitiesWithStatus('NEW'),
        this.hasActivitiesWithStatus('IN_PROGRESS'),
      ]).pipe(
        map(([newActivities, inProgressActivities]) => newActivities || inProgressActivities),
        catchError(() => of(false))
      )
    )),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * What the Activities nav item shows: a plain dot when either kind of work is outstanding. The two
   * lists live behind the same nav entry, so one indicator covers both.
   */
  readonly activitiesNavBadge$: Observable<boolean> = combineLatest([
    this.posIntegrityBadge$,
    this.userActivitiesBadge$,
  ]).pipe(
    map(([posIntegrity, userActivities]) => posIntegrity.hasTasks || userActivities),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly requestedSurveysBadge$: Observable<boolean> = timer(0, this.intervalDuration).pipe(
    switchMap(() => this.whenVisible(
      this.surveyService.getRequestedSurveyCount({
        statuses: [1,2],
        retailerDisabled: 'N',
        territoryNumber: this.terrService.getTerritory(),
        startDate: this.today(),
        endDate: this.today(),
        page: 0,
        size: 1,
        sort: ['endDate,asc'],
      }).pipe(
        map(value => (value.totalElements ?? value.numberOfElements ?? value.content?.length ?? 0) > 0),
        catchError(() => of(false))
      )
    )),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    private readonly chatSvc: ChatService,
    private readonly posIntegrityService: PosIntegrityService,
    private readonly terrService: TerritoryService,
    private readonly surveyService: SurveyService,
    private readonly userActivityService: UserActivityService,
  ) {}

  private hasActivitiesWithStatus(status: ActivityStatus): Observable<boolean> {
    return this.userActivityService
      // skipLoader: this runs on a timer, so it must never raise the global loading overlay.
      .getActivitiesByTerritory(this.terrService.getTerritory(), new Pageable(0, 1), undefined, status, true)
      .pipe(map(page => (page.totalElements ?? page.content?.length ?? 0) > 0));
  }

  private whenVisible<T>(request$: Observable<T>): Observable<T> {
    return document.visibilityState === 'visible' ? request$ : EMPTY;
  }

  private today(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
